import {getGaClientId} from '@optimaxdev/analytics/desktop/lib/clientId/index';
import {createAction, createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import FormData from 'form-data';

import {ENDPOINTS} from 'constants/endpoints';
import {formatAndCheckAddress} from 'desktop/pages/cart/components/applePayExpressButton/utils/applePayExpressFormatters';
import {logApplePayError} from 'desktop/pages/cart/components/applePayExpressButton/utils/applePayLogger';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';
import {ApplePayPaymentInfoType} from 'types/applePayType';

type CompleteOrderType = {
    error: boolean;
    success: boolean;
    error_messages: string;
    go_to: string;
    status: 'fulfilled' | 'rejected' | 'pending' | '';
    errors?: Array<{
        title: string;
        friendlyMessage: string;
        detail?: {
            order_id?: string;
            orders_count?: number;
        };
    }>;
    riskified_message?: string;
    riskified_status?: string;
    riskified_deco_eligible?: boolean;
};

export type ApplePayType = {
    loaded: boolean;
    errorMessage?: string;
    order?: CompleteOrderType | null;
};

export const initialState: ApplePayType = {
    errorMessage: '',
    loaded: false,
    order: null,
};

export const saveApplePayExpress = createAction<ApplePayPaymentInfoType>('APPLEPAY_EXPRESS@SAVE');

export const setApplePayExpressError = createAction<string>('APPLEPAY_EXPRESS@SET_ERROR');

export const setLoadedApplePayExpress = createAction<boolean>('APPLEPAY_EXPRESS@SET_LOADED');

export const saveApplePayExpressPayment = createAsyncThunk<
    {data: {message: string}},
    ApplePayPaymentInfoType,
    {rejectValue: {response: {data: {message: string}}}}
>('APPLEPAY_EXPRESS@SAVE_PAYMENT', async (paymentInfo, {rejectWithValue}) => {
    const data = formatAndCheckAddress(paymentInfo);

    data.append('payment[method]', 'stripe_apple_pay');
    data.append('payment[payment_method_id]', paymentInfo.token || '');
    data.append('payment[token]', paymentInfo.token || '');

    return await apiConfig.instanceGusaBackend
        .request({
            data,
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.CHECKOUT_SET_PAYMENT,
        })
        .catch(e => {
            const errorText = 'Could not save Apple Pay payment';
            logApplePayError(e, errorText);
            return rejectWithValue(e);
        });
});

export const completeOrderReviewApplePayExpress = createAsyncThunk<
    {data: CompleteOrderType},
    {affid: string; method: string},
    {rejectValue: {response: {data: CompleteOrderType}}}
>('APPLEPAY_EXPRESS@COMPLETE_ORDER_REVIEW', async (paymentInfo, {rejectWithValue}) => {
    try {
        const data = new FormData();

        data.append('subscribe-newsletter', 0);
        data.append('payment[method]', paymentInfo.method || '');
        data.append('payment[terms]', 1);
        data.append('affid', paymentInfo.affid || '');
        data.append('ga_cid', getGaClientId());

        return await apiConfig.instanceGusaBackend.request({
            data,
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: `${ENDPOINTS.CHECKOUT_PLACE_ORDER}`,
        });
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const applePayExpress = createReducer(initialState, builder =>
    builder
        .addCase(completeOrderReviewApplePayExpress.fulfilled, (state, action) => ({
            ...state,
            order: action.payload.data,
        }))
        .addCase(completeOrderReviewApplePayExpress.rejected, (state, action) => ({
            ...state,
            loaded: false,
            order: action.payload?.response?.data,
        }))
        .addCase(saveApplePayExpressPayment.rejected, (state, action) => ({
            ...state,
            errorMessage: action.payload?.response?.data?.message,
            loaded: false,
        }))
        .addCase(setApplePayExpressError, (state, action) => ({
            ...state,
            errorMessage: action.payload,
        }))
        .addCase(setLoadedApplePayExpress, (state, action) => ({
            ...state,
            loaded: action.payload,
        })),
);
